import { HTMLAttributes, Key } from "react";
import TextField from "@mui/material/TextField";
import { Autocomplete, Chip, Tooltip } from "@mui/material";
import type { OptionValue } from "../fieldTypes";

export interface AutocompleteOption {
  id?: OptionValue;
  value: OptionValue;
  label?: string;
  caption?: string;
}

export type AutocompleteItem = AutocompleteOption | string;
export type AutocompleteValue = AutocompleteItem | AutocompleteItem[] | null;

interface AutocompleteFieldProps {
  options: AutocompleteItem[];
  label: string;
  name?: string;
  id?: string;
  width?: string;
  fullWidth?: boolean;
  value: AutocompleteValue;
  multiple?: boolean;
  loading?: boolean;
  disableListWrap?: boolean;
  onChange: (value: AutocompleteValue) => void;
  onSearch: (value: string) => void;
}

const isAutocompleteOption = (
  item: AutocompleteItem
): item is AutocompleteOption => typeof item === "object";

const getOptionText = (item: AutocompleteItem): string =>
  isAutocompleteOption(item) ? item.label || item.caption || "" : item;

const getOptionValue = (item: AutocompleteItem): OptionValue =>
  isAutocompleteOption(item) ? item.value : item;

const AutocompleteField = ({
  options,
  label,
  id,
  multiple = false,
  loading = false,
  value,
  fullWidth = false,
  onChange,
  onSearch,
  disableListWrap = false,
}: AutocompleteFieldProps) => {
  const truncate = (input: string): string => {
    if (input.length > 15) {
      return `${input.substring(0, 15)}...`;
    }
    return input;
  };

  const getMultiple = (data: AutocompleteItem[]): AutocompleteItem[] => {
    const allOption = options.find(
      (option) => getOptionValue(option) === "all"
    );
    const hasAll = !!allOption;
    let nextValue = data;
    if (hasAll) {
      if (data.length === 0) {
        nextValue = [allOption];
      } else if (getOptionValue(data[0]) === "all") {
        nextValue = data.filter((item) => getOptionValue(item) !== "all");
      } else if (getOptionValue(data[data.length - 1]) === "all") {
        nextValue = [allOption];
      } else {
        nextValue = data;
      }
    }
    return nextValue;
  };

  return (
    <Autocomplete
      disablePortal
      disableListWrap={disableListWrap}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      options={options}
      id={id}
      sx={{ minWidth: 200, ".MuiAutocomplete-tag": { margin: 0 } }}
      loading={loading}
      size="small"
      value={value}
      fullWidth={fullWidth}
      filterOptions={(x) => x}
      onChange={(_e, data) => {
        if (multiple) {
          onChange(getMultiple(Array.isArray(data) ? data : []));
        } else {
          onChange(data);
        }
      }}
      limitTags={2}
      getOptionLabel={getOptionText}
      onInputChange={(_event, newInputData) => onSearch(newInputData)}
      isOptionEqualToValue={(option, selectedValue) =>
        getOptionValue(option) === getOptionValue(selectedValue)
      }
      renderInput={(params) => <TextField {...params} label={label} />}
      renderOption={(
        props: HTMLAttributes<HTMLLIElement> & { key: Key },
        option
      ) => (
        <li {...props} key={props.key} value={getOptionValue(option)}>
          {getOptionText(option)}
        </li>
      )}
      renderValue={(tagValue, getItemProps) => {
        const values = Array.isArray(tagValue) ? tagValue : [tagValue];

        return values.map((option, index) => (
          <Tooltip key={getOptionValue(option)} title={getOptionText(option)}>
            <Chip
              label={truncate(getOptionText(option))}
              size="small"
              {...getItemProps({ index })}
            />
          </Tooltip>
        ));
      }}
    />
  );
};
export { AutocompleteField };
